import { Rocket } from "lucide-react";
import React, { useMemo } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatRub } from "@/lib/format";
import type { InvoiceSummary } from "@/types/api";

interface Props {
  open: boolean;
  invoices: InvoiceSummary[];
  onConfirm: () => void;
  onClose: () => void;
}

interface Bucket {
  label: string;
  count: number;
  total: number;
}

export function BatchCreateConfirmDialog({
  open,
  invoices,
  onConfirm,
  onClose,
}: Props): React.ReactElement {
  const ready = useMemo(
    () => invoices.filter((i) => i.readiness === "ready"),
    [invoices],
  );
  const skipped = useMemo(
    () => invoices.filter((i) => i.readiness !== "ready"),
    [invoices],
  );

  const byKontragent = useMemo<Bucket[]>(() => {
    const map = new Map<string, Bucket>();
    for (const inv of ready) {
      const key = inv.creator_name ?? inv.seller_name ?? "—";
      const amt = Number(inv.total_with_vat ?? 0);
      const b = map.get(key);
      if (b) {
        b.count += 1;
        b.total += Number.isFinite(amt) ? amt : 0;
      } else {
        map.set(key, { label: key, count: 1, total: amt });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [ready]);

  const grandTotal = byKontragent.reduce((sum, b) => sum + b.total, 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="size-5" />
            Создание документов в 1С
          </DialogTitle>
          <DialogDescription>
            Сводка по выбранным файлам. После подтверждения запустится
            последовательное создание — можно будет отслеживать прогресс.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <div className="flex items-baseline justify-between">
              <span className="text-muted-foreground">К созданию</span>
              <span className="text-lg font-semibold">
                {ready.length}{" "}
                {ready.length === 1 ? "документ" : ready.length < 5 ? "документа" : "документов"}
              </span>
            </div>
            <div className="mt-1 flex items-baseline justify-between">
              <span className="text-muted-foreground">Суммарно</span>
              <span className="font-mono">{formatRub(grandTotal)}</span>
            </div>
            {skipped.length > 0 && (
              <div className="mt-2 text-xs text-amber-700">
                {skipped.length}{" "}
                {skipped.length === 1 ? "файл не готов" : "файла(ов) не готовы"} —
                пропустим.
              </div>
            )}
          </div>

          {byKontragent.length > 0 && (
            <div>
              <div className="mb-2 text-xs font-medium uppercase text-muted-foreground">
                По контрагентам
              </div>
              <ul className="space-y-1 text-sm">
                {byKontragent.map((b) => (
                  <li
                    key={b.label}
                    className="flex items-baseline justify-between gap-4"
                  >
                    <span className="truncate">{b.label}</span>
                    <span className="whitespace-nowrap text-muted-foreground">
                      {b.count} · {formatRub(b.total)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={onConfirm}
            disabled={ready.length === 0}
            className="gap-2"
          >
            <Rocket className="size-4" />
            Создать {ready.length}{" "}
            {ready.length === 1 ? "документ" : "документов"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
